import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";

export default function CashDrawer() {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [start, setStart] = useState("");
  const [closeCount, setCloseCount] = useState("");

  const load = useCallback(() => {
    api.get("/cash-drawer/current").then((r) => setCurrent(r.data)).catch(() => {});
    api.get("/cash-drawer/history").then((r) => setHistory(r.data)).catch(() => setHistory([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = () => api.post("/cash-drawer/open", { starting_cash: +start }).then(() => { setStart(""); load(); toast.success("Drawer opened"); }).catch((e) => toast.error(formatApiError(e)));
  const close = () => api.post("/cash-drawer/close", { closing_count: +closeCount }).then((r) => { setCloseCount(""); load(); toast.success(`Closed · variance ${money(r.data.variance)}`); }).catch((e) => toast.error(formatApiError(e)));

  const expected = current ? (current.starting_cash + current.cash_sales_total + (current.split_cash_total || 0)) : 0;

  return (
    <div className="max-w-5xl space-y-6">
      {current ? (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /><span className="font-semibold text-slate-800">Drawer open</span><span className="text-xs text-slate-400">by {current.opened_by} · {fmtDate(current.opened_at)}</span></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Starting cash" value={money(current.starting_cash)} />
            <Stat label="Cash sales" value={money(current.cash_sales_total)} />
            <Stat label="Credit sales" value={money(current.credit_sales_total)} />
            <Stat label="Flower sales" value={money(current.flower_sales_total)} />
            <Stat label="Split cash" value={money(current.split_cash_total || 0)} />
            <Stat label="Sales count" value={current.sales_count || 0} />
            <Stat label="Expected cash" value={money(expected)} highlight />
          </div>
          <div className="mt-5 flex items-end gap-3 border-t pt-4">
            <div className="flex-1 max-w-xs"><Label className="text-xs">Closing count ($)</Label><Input type="number" value={closeCount} onChange={(e) => setCloseCount(e.target.value)} data-testid="close-count-input" /></div>
            <Button onClick={close} variant="destructive" data-testid="close-drawer-btn"><Lock className="w-4 h-4 mr-1.5" />Close drawer</Button>
          </div>
        </Card>
      ) : (
        <Card className="p-6 max-w-md">
          <div className="flex items-center gap-2 mb-4"><Wallet className="w-5 h-5 text-slate-400" /><span className="font-semibold">No drawer open</span></div>
          <Label className="text-xs">Starting cash ($)</Label>
          <Input type="number" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 mb-3" data-testid="start-cash-input" />
          <Button onClick={open} className="bg-indigo-600" data-testid="open-drawer-btn"><Unlock className="w-4 h-4 mr-1.5" />Open drawer</Button>
        </Card>
      )}

      <div>
        <h3 className="font-semibold text-slate-800 mb-3">Drawer history</h3>
        <Card className="divide-y">
          {history.filter((h) => h.status === "closed").map((h) => (
            <div key={h.id} className="grid grid-cols-2 md:grid-cols-5 gap-2 p-3 text-sm" data-testid={`drawer-history-${h.id}`}>
              <div className="text-slate-500">{fmtDate(h.opened_at)}</div>
              <div>Start {money(h.starting_cash)}</div>
              <div>Expected {money(h.expected_cash)}</div>
              <div>Counted {money(h.closing_count)}</div>
              <div className={h.variance === 0 ? "text-slate-500" : h.variance < 0 ? "text-red-600" : "text-emerald-600"}>Variance {money(h.variance)}</div>
            </div>
          ))}
          {history.filter((h) => h.status === "closed").length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No closed drawers yet.</div>}
        </Card>
      </div>
    </div>
  );
}

const Stat = ({ label, value, highlight }) => (
  <div className={`rounded-lg p-3 ${highlight ? "bg-indigo-50" : "bg-slate-50"}`}>
    <div className="text-xs text-slate-500">{label}</div>
    <div className={`text-lg font-bold ${highlight ? "text-indigo-700" : "text-slate-800"}`}>{value}</div>
  </div>
);
